import * as Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import type { ReactNode } from "react";

import CloseButton from "../CloseButton";
import HelpIcon from "../help/HelpIcon";

import { Flex, Theme } from "@/compat/radix-themes";

interface DialogBoxProps {
  title: ReactNode;
  description?: string;
  /** Optional icon rendered inline before the title (design-system compact header). */
  headerIcon?: ReactNode;
  /**
   * Compact header matching the design-system dialog recipe (the Version View
   * dialog): px-5 py-2.5, title text-[13px] font-semibold, description
   * text-[11px]. Defaults to false (the larger text-base header) for
   * backward compatibility.
   */
  compact?: boolean;
  /** Optional rich help content rendered as a `?` icon next to the title. */
  helpContent?: ReactNode;
  /** Optional URL for a "Learn more" link inside the help tooltip. */
  helpLink?: string;
  trigger?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  contentStyle?: React.CSSProperties;
  className?: string;
  overlayClassName?: string;
  /** Extra classes for the footer wrapper (e.g. background color). */
  footerClassName?: string;
  /** Allow closing the dialog by clicking the backdrop. Default: false (preserves existing behavior). */
  dismissOnOverlayClick?: boolean;
  /** Prevent any close action while a blocking operation is running. */
  closeDisabled?: boolean;
  /** Size preset. Defaults to "full" for backward compatibility (80vw x 90vh). */
  size?: "sm" | "md" | "lg" | "xl" | "full";
  /** Optional tour anchor — forwarded as data-tour on Dialog.Content for TourOverlay spotlighting. */
  "data-tour"?: string;
}

const SIZE_MAP: Record<
  NonNullable<DialogBoxProps["size"]>,
  { width: string; height: string }
> = {
  sm: { width: "420px", height: "auto" },
  md: { width: "560px", height: "auto" },
  lg: { width: "720px", height: "auto" },
  xl: { width: "960px", height: "auto" },
  full: { width: "80vw", height: "90vh" },
};

const DialogBox = ({
  title,
  description,
  headerIcon,
  compact = false,
  helpContent,
  helpLink,
  trigger,
  children,
  footer,
  open,
  onOpenChange,
  contentStyle,
  className,
  overlayClassName,
  footerClassName,
  dismissOnOverlayClick = false,
  closeDisabled = false,
  size = "full",
  "data-tour": dataTour,
}: DialogBoxProps) => {
  const preset = SIZE_MAP[size];
  const handleOpenChange = (nextOpen: boolean) => {
    if (closeDisabled && !nextOpen) return;
    onOpenChange?.(nextOpen);
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      {trigger && (
        <Theme>
          <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>{" "}
        </Theme>
      )}

      <Dialog.Portal>
        <Theme>
          <Dialog.Overlay
            className={clsx(
              "bg-black/40 backdrop-blur-sm fixed inset-0 z-[99990]",
              "data-[state=open]:animate-overlay-in data-[state=closed]:animate-overlay-out",
              overlayClassName,
            )}
          />

          <Dialog.Content
            data-tour={dataTour}
            className={clsx(
              "bg-white rounded-lg shadow-2xl ring-1 ring-black/5 z-[99995]",
              "fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]",
              "focus:outline-none flex flex-col overflow-hidden",
              "data-[state=open]:animate-dialog-in data-[state=closed]:animate-dialog-out",
              className,
            )}
            onPointerDownOutside={(e) => {
              if (closeDisabled || !dismissOnOverlayClick) e.preventDefault();
            }}
            onInteractOutside={(e) => {
              if (closeDisabled || !dismissOnOverlayClick) e.preventDefault();
            }}
            onEscapeKeyDown={(e) => {
              if (closeDisabled) e.preventDefault();
            }}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: contentStyle?.width ?? preset.width,
              height: contentStyle?.height ?? preset.height,
              maxWidth: contentStyle?.maxWidth ?? "calc(100vw - 2rem)",
              maxHeight: contentStyle?.maxHeight ?? "calc(100vh - 2rem)",
              marginRight: contentStyle?.marginRight ?? undefined,
              ...contentStyle,
            }}
          >
            {/* Header */}
            {title && (
              <div
                className={clsx(
                  "shrink-0 border-b border-border",
                  compact ? "px-5 py-2.5" : "px-6 py-3",
                )}
              >
                <Flex justify="between" align="start" gap="3">
                  <Flex
                    direction="column"
                    gap={compact ? "0" : "1"}
                    className="min-w-0"
                  >
                    <Dialog.Title
                      className={clsx(
                        "tracking-tight text-text-primary inline-flex items-center gap-1.5",
                        compact
                          ? "text-[13px] font-semibold"
                          : "text-base font-semibold",
                      )}
                    >
                      {headerIcon}
                      {title}
                      {helpContent && (
                        <HelpIcon
                          content={helpContent}
                          learnMoreHref={helpLink}
                          side="bottom"
                        />
                      )}
                    </Dialog.Title>
                    {description && (
                      <Dialog.Description
                        className={clsx(
                          "text-text-secondary leading-relaxed",
                          compact ? "text-[11px]" : "text-xs",
                        )}
                      >
                        {description}
                      </Dialog.Description>
                    )}
                  </Flex>
                  <Dialog.Close asChild>
                    <CloseButton
                      disabled={closeDisabled}
                      size={compact ? 14 : 16}
                    />
                  </Dialog.Close>
                </Flex>
              </div>
            )}

            {/* Scrollable content area */}
            <Flex
              direction="column"
              className={`flex-1 min-h-0 ${contentStyle?.overflow === "visible" ? "overflow-visible" : "overflow-auto"}`}
            >
              {children}
            </Flex>

            {/* Footer */}
            {footer && (
              <div
                className={clsx(
                  "px-6 py-2.5 shrink-0 border-t border-border",
                  footerClassName,
                )}
              >
                {footer}
              </div>
            )}
          </Dialog.Content>
        </Theme>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default DialogBox;
